import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BulkClientUpload = ({ onUpload, onCancel }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState([]);

  // Función para descargar el archivo de ejemplo
  const downloadTemplate = () => {
    const templateData = [
      {
        'name': 'Clínica Dental Sonrisa',
        'specialty': 'Odontología',
        'cell': 'C1GA;G1M1',
        'email': 'contacto@clinicasonrisa.com',
        'country': 'México',
        'googleAdsLink': 'https://ads.google.com/ejemplo1',
        'googleMapsLink': 'https://maps.google.com/ejemplo1',
        'facebookLink': 'https://facebook.com/clinicasonrisa',
        'metaAccountLink': 'https://business.facebook.com/ejemplo1',
        'salesExecutive': 'Juan Pérez;María López'
      },
      {
        'name': 'Centro Médico Familiar',
        'specialty': 'Medicina General',
        'cell': 'C2GA',
        'email': 'info@centromedico.com',
        'country': 'Colombia',
        'googleAdsLink': '',
        'googleMapsLink': 'https://maps.google.com/ejemplo2',
        'facebookLink': '',
        'metaAccountLink': '',
        'salesExecutive': 'María García'
      },
      {
        'name': 'Dr. Juan Pérez Consultorio',
        'specialty': 'Cardiología',
        'cell': 'C1GA;C2GA;G1M1;G1M2',
        'email': 'dr.perez@cardio.com',
        'country': 'España',
        'googleAdsLink': 'https://ads.google.com/ejemplo3',
        'googleMapsLink': 'https://maps.google.com/ejemplo3',
        'facebookLink': 'https://facebook.com/drperez',
        'metaAccountLink': 'https://business.facebook.com/ejemplo3',
        'salesExecutive': 'Carlos López;Ana Martín'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Plantilla Clientes');

    // Ajustar el ancho de las columnas
    const columnWidths = [
      { wch: 30 }, // name
      { wch: 20 }, // specialty
      { wch: 25 }, // cell
      { wch: 30 }, // email
      { wch: 15 }, // country
      { wch: 35 }, // googleAdsLink
      { wch: 35 }, // googleMapsLink
      { wch: 35 }, // facebookLink
      { wch: 35 }, // metaAccountLink
      { wch: 25 }  // salesExecutive
    ];
    worksheet['!cols'] = columnWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'plantilla_clientes.xlsx');
  };

  // Función para manejar la selección de archivo
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
    setPreviewData([]);

    if (selectedFile) {
      // Validar que sea un archivo Excel
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(selectedFile.type)) {
        setError('Por favor, selecciona un archivo Excel válido (.xlsx o .xls)');
        setFile(null);
        return;
      }

      // Previsualizar los datos
      previewFile(selectedFile);
    }
  };

  // Función para previsualizar el archivo
  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Mostrar solo los primeros 5 registros para previsualización
        setPreviewData(jsonData.slice(0, 5));
      } catch (error) {
        setError('Error al leer el archivo Excel. Verifica que el formato sea correcto.');
        console.error('Error al leer Excel:', error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Función para procesar el archivo Excel
  const processExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const processedClients = [];

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            
            // Mapear las columnas del Excel a los campos exactos de la base de datos
            const clientName = row['name'] || row['Nombre del Cliente'] || row['Nombre'] || row['Cliente'] || '';
            
            if (!clientName || clientName.trim() === '') {
              reject(new Error(`Error en la fila ${i + 2}: El nombre del cliente es obligatorio.`));
              return;
            }

            // Procesar células múltiples
            const cellsString = row['cell'] || row['Células (separadas por ;)'] || row['Células'] || row['Teléfonos'] || '';
            const cellsArray = cellsString ? 
              cellsString.split(';').map(c => c.trim()).filter(c => c !== '') : [];

            // Procesar ejecutivos de ventas múltiples
            const salesExecString = row['salesExecutive'] || row['Ejecutivo de Ventas'] || row['Ejecutivos'] || '';
            const salesExecArray = salesExecString ? 
              salesExecString.split(';').map(s => s.trim()).filter(s => s !== '') : [];

            const client = {
              name: clientName.trim(),
              specialty: (row['specialty'] || row['Especialidad'] || '').trim(),
              cell: cellsArray.length > 0 ? cellsArray.join(',') : '',
              cells: cellsArray,
              email: (row['email'] || row['Email'] || '').trim(),
              country: (row['country'] || row['País'] || 'México').trim(),
              salesExecutive: salesExecArray.length > 0 ? salesExecArray.join(',') : '',
              salesExecutives: salesExecArray,
              googleAdsLink: (row['googleAdsLink'] || row['Link Google Ads'] || '').trim(),
              googleMapsLink: (row['googleMapsLink'] || row['Link Google Maps'] || '').trim(),
              facebookLink: (row['facebookLink'] || row['Link Facebook'] || '').trim(),
              metaAccountLink: (row['metaAccountLink'] || row['Link Meta Account'] || '').trim(),
              createdAt: new Date().toISOString()
            };

            processedClients.push(client);
          }

          resolve(processedClients);
        } catch (error) {
          reject(new Error(`Error al procesar el archivo Excel: ${error.message}`));
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Función para manejar el envío
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!file) {
      setError('Por favor, selecciona un archivo Excel.');
      return;
    }

    setIsProcessing(true);

    try {
      const clients = await processExcelFile(file);
      
      if (clients.length === 0) {
        setError('No se encontraron clientes válidos en el archivo.');
        setIsProcessing(false);
        return;
      }

      console.log('Enviando clientes para carga masiva:', clients);
      await onUpload(clients);
      
      // Limpiar el formulario después del éxito
      setFile(null);
      setPreviewData([]);
      // Limpiar el input file
      const fileInput = document.getElementById('excel-file-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error en carga masiva:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-[#0061A8] mb-4">Carga Masiva de Clientes</h2>
      
      {/* Botón para descargar plantilla */}
      <div className="mb-6 p-4 bg-[#F8F9FA] border border-[#D1D3D4] rounded-md">
        <h3 className="text-lg font-medium text-[#0061A8] mb-2">📋 Paso 1: Descarga la Plantilla</h3>
        <p className="text-sm text-[#63666A] mb-3">
          Descarga el archivo de ejemplo para ver el formato correcto y llenar tus datos.
        </p>
        <button
          type="button"
          onClick={downloadTemplate}
          className="bg-[#A8D08D] text-[#2D5016] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors flex items-center"
        >
          📥 Descargar Plantilla Excel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Selector de archivo */}
        <div>
          <h3 className="text-lg font-medium text-[#0061A8] mb-2">📂 Paso 2: Selecciona tu Archivo</h3>
          <label className="block text-sm font-medium text-[#63666A] mb-2">
            Selecciona el archivo Excel con los datos de los clientes:
          </label>
          <input
            id="excel-file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="block w-full text-sm text-[#63666A] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#0061A8] file:text-white hover:file:bg-opacity-90"
          />
          <p className="text-xs text-[#63666A] mt-1">
            Formatos soportados: .xlsx, .xls
          </p>
        </div>

        {/* Previsualización de datos */}
        {previewData.length > 0 && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-[#0061A8] mb-2">👀 Previsualización</h3>
            <p className="text-sm text-[#63666A] mb-2">
              Primeros {previewData.length} registros del archivo:
            </p>
            <div className="overflow-x-auto border border-[#D1D3D4] rounded-md">
              <table className="min-w-full text-xs">
                <thead className="bg-[#F8F9FA]">
                  <tr>
                    <th className="px-2 py-1 text-left">Nombre</th>
                    <th className="px-2 py-1 text-left">Especialidad</th>
                    <th className="px-2 py-1 text-left">Células</th>
                    <th className="px-2 py-1 text-left">Email</th>
                    <th className="px-2 py-1 text-left">País</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index} className="border-t border-[#D1D3D4]">
                      <td className="px-2 py-1">{row['name'] || row['Nombre del Cliente'] || row['Nombre'] || row['Cliente'] || ''}</td>
                      <td className="px-2 py-1">{row['specialty'] || row['Especialidad'] || ''}</td>
                      <td className="px-2 py-1">{row['cell'] || row['Teléfonos (separados por ;)'] || row['Teléfonos'] || row['Células'] || ''}</td>
                      <td className="px-2 py-1">{row['email'] || row['Email'] || ''}</td>
                      <td className="px-2 py-1">{row['country'] || row['País'] || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
            disabled={isProcessing}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!file || isProcessing}
            className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Procesando...
              </>
            ) : (
              '📤 Cargar Clientes'
            )}
          </button>
        </div>
      </form>

      {/* Información adicional */}
      <div className="mt-6 p-4 bg-[#E8F4FD] border border-[#B3D9F2] rounded-md">
        <h4 className="text-sm font-medium text-[#0061A8] mb-2">💡 Consejos para una carga exitosa:</h4>
        <ul className="text-xs text-[#63666A] space-y-1">
          <li>• Usa la plantilla descargada como base para tus datos</li>
          <li>• El campo "name" (nombre del cliente) es obligatorio</li>
          <li>• Para múltiples células, sepáralas con punto y coma (;)</li>
          <li>• Para múltiples ejecutivos de ventas, sepáralos con punto y coma (;)</li>
          <li>• Los campos vacíos se llenarán con valores por defecto</li>
          <li>• Revisa la previsualización antes de cargar</li>
        </ul>
      </div>
    </div>
  );
};

export default BulkClientUpload;
